import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { db } from '../../firebase/config.js'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import Modal from '../common/Modal'
import QuestionTypeTag from '../Questionnaire/QuestionTypeTag'
import EffectMeta from '../effects/EffectMeta'
import groupFieldsBySection from '../../helpers/questionnaire/groupFieldsBySection'
import triggersLostByExclusion from '../../helpers/questionnaire/triggersLostByExclusion'
import summarizeMomentQuestions from '../../helpers/questionnaire/summarizeMomentQuestions'
import isStandardQuestion from '../../helpers/questionnaire/isStandardQuestion'
import isQualifyingQuestion from '../../helpers/questionnaire/isQualifyingQuestion'
import useMomentHasResponses from '../../hooks/useMomentHasResponses'

// Per meetmoment kiezen welke vragen gesteld worden. De selectie leeft als
// concept in state en gaat pas bij 'Opslaan' in één schrijfactie naar Firestore:
// je vinkt er meestal meerdere tegelijk uit, 'Overnemen van…' verandert de hele
// lijst in één klik, en 'Annuleren' moet ook echt iets betekenen.
//
// Alleen EIGEN vragen zijn uit te zetten. Meetstandaardvragen staan op elk
// meetmoment aan: een MSI-effect wordt afgemeten aan het verschil tussen voor-
// en nameting, dus die vraag moet op beide momenten gesteld zijn.
//
// Opgeslagen worden de UITSLUITINGEN, niet de insluitingen — zie
// docs/features/questions-per-meetmoment.md.
//
// Kwalificeringsvragen zijn hier ook vergrendeld, maar andersom: ze staan
// alleen op het eerste meetmoment aan, en dat houdt de schakelaar op het
// onderzoek bij (`syncQualifyingExclusions`). Hun stand komt dus uit wat er
// opgeslagen is, zit nooit in het concept, en gaat bij opslaan ongewijzigd terug.
// AI-CONTEXT: See docs/features/sector-kwalificeringsvragen.md

/**
 * @typedef {object} MeasureMomentDoc
 * @property {string} docid - Firestore doc-id (schrijfdoel)
 * @property {string} ID - Businesskey van het meetmoment
 * @property {string} Title - Titel van het meetmoment
 * @property {number} [Responses] - Aantal ingezonden antwoorden op dit moment
 * @property {Array<string>} [ExcludedFieldIDs] - Uitgezette vragen
 */

/**
 * @param {object} props
 * @param {boolean} props.modalOpen - Of de modal open staat
 * @param {(open: boolean) => void} props.setModalOpen - Sluit/opent de modal
 * @param {MeasureMomentDoc} props.moment - Het meetmoment dat bewerkt wordt
 * @param {Array<object>} props.fields - QuestionnaireFields van de gekoppelde
 *   vragenlijst, in Position-volgorde (inclusief sectiemarkeringen)
 * @param {Array<MeasureMomentDoc>} [props.siblingMoments] - De andere
 *   meetmomenten van hetzelfde onderzoek, voor 'Overnemen van…'
 */
const MeasureMomentQuestionsModal = ({
    modalOpen,
    setModalOpen,
    moment,
    fields,
    siblingMoments = [],
}) => {
    // Context
    const [, setSaved] = useContext(SavedIcon)

    // State
    const [excluded, setExcluded] = useState(/** @type {Set<string>} */ (new Set()))
    const [search, setSearch] = useState('')
    const [filter, setFilter] = useState('all')
    const [saveError, setSaveError] = useState('')

    const savedExcluded = useMemo(
        () => (moment?.ExcludedFieldIDs || []).filter(Boolean),
        [moment?.ExcludedFieldIDs]
    )

    // Een vergrendelde vraag hoort nooit in de lijst te staan; stond hij er nog
    // in (oude data), dan valt hij er hier uit en corrigeert opslaan dat.
    const lockedIds = useMemo(
        () => new Set((fields || []).filter(isStandardQuestion).map((field) => field.ID)),
        [fields]
    )

    const qualifyingIds = useMemo(
        () => new Set((fields || [])
            .filter((field) => isQualifyingQuestion(field) && field.Type !== 'section')
            .map((field) => field.ID)),
        [fields]
    )
    // De opgeslagen stand van de kwalificeringsvragen op dit meetmoment.
    const qualifyingOff = useMemo(
        () => savedExcluded.filter((id) => qualifyingIds.has(id)),
        [savedExcluded, qualifyingIds]
    )

    // Het concept wordt ALLEEN gevuld bij het openen van een (ander) meetmoment.
    // Niet bij elke wijziging van de onderliggende data: de Firestore-listener
    // levert bij elke snapshot een nieuw object, en de oudercomponent bouwt de
    // veldenlijst per render opnieuw op. Zou de vulling daarop meelopen, dan
    // veegt een titelwijziging van een collega je halve selectie weg — en
    // schrijft de volgende klik op Opslaan iets anders weg dan je ziet.
    const latest = useRef({ savedExcluded, lockedIds, qualifyingIds })
    latest.current = { savedExcluded, lockedIds, qualifyingIds }

    useEffect(() => {
        const { savedExcluded: saved, lockedIds: locked, qualifyingIds: qualifying } = latest.current
        setExcluded(new Set(saved.filter((id) => !locked.has(id) && !qualifying.has(id))))
        setSearch('')
        setFilter('all')
        setSaveError('')
    }, [modalOpen, moment?.ID])

    // Hooks
    // Alleen JA/NEE: een telling zou elk antwoorddocument van dit meetmoment
    // ophalen, en dat voor één waarschuwing.
    const hasResponses = useMomentHasResponses(modalOpen ? (moment?.ID || '') : '')
    const groups = useMemo(() => groupFieldsBySection(fields), [fields])
    // Wat de respondent op dit meetmoment mist: het concept plus de vaste stand
    // van de kwalificeringsvragen.
    const effectiveExcluded = useMemo(
        () => [...excluded, ...qualifyingOff],
        [excluded, qualifyingOff]
    )
    const summary = useMemo(
        () => summarizeMomentQuestions(fields, effectiveExcluded),
        [fields, effectiveExcluded]
    )

    // Bijna elke vraag hangt aan een effect, dus een waarschuwing per regel is
    // geen signaal meer. Eén keer samenvatten wat het uitzetten kost.
    const offEffectQuestions = useMemo(() => (fields || []).filter((field) => (
        field?.Type !== 'section'
        && excluded.has(field?.ID)
        && (field?.EffectId?.length > 0 || field?.Indicator === 'true')
    )).length, [fields, excluded])

    // Zet je een triggervraag uit, dan kan de toon-als-regel niet meer beoordeeld
    // worden. Het leespad faalt bewust open — de vervolgvraag wordt dan gewoon
    // aan iedereen gesteld. Dat is iets anders dan de bouwer instelde, dus
    // melden.
    // AI-CONTEXT: See docs/features/conditional-questions.md
    const lostTriggers = useMemo(
        () => triggersLostByExclusion(fields, effectiveExcluded),
        [fields, effectiveExcluded]
    )

    const hasLocked = lockedIds.size > 0
    const nothingToChoose = summary.total > 0 && lockedIds.size + qualifyingIds.size === summary.total
    const isFixed = (id) => lockedIds.has(id) || qualifyingIds.has(id)
    const isActiveQuestion = (id) => lockedIds.has(id) || !effectiveExcluded.includes(id)

    const dirty = useMemo(() => {
        if (effectiveExcluded.length !== savedExcluded.length) return true
        return savedExcluded.some((id) => !effectiveExcluded.includes(id))
    }, [effectiveExcluded, savedExcluded])

    // Handlers
    const toggleQuestion = (id) => {
        setExcluded((previous) => {
            const next = new Set(previous)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    // Een sectie die (deels) aanstaat gaat helemaal uit, anders helemaal aan.
    const toggleSection = (questions) => {
        const switchable = questions.filter((question) => !isFixed(question.ID))
        setExcluded((previous) => {
            const next = new Set(previous)
            const anyActive = switchable.some((question) => !next.has(question.ID))
            switchable.forEach((question) => {
                if (anyActive) next.add(question.ID)
                else next.delete(question.ID)
            })
            return next
        })
    }

    const allOn = () => setExcluded(new Set())

    const copyFromMoment = (e) => {
        const source = siblingMoments.find((sibling) => sibling.ID === e.target.value)
        if (!source) return
        setExcluded(new Set((source.ExcludedFieldIDs || []).filter((id) => !qualifyingIds.has(id))))
    }

    const save = () => {
        setSaveError('')
        db.collection('MeasureMoments')
            .doc(moment.docid)
            .update({ ExcludedFieldIDs: [...[...excluded].filter((id) => !isFixed(id)), ...qualifyingOff] })
            .then(() => {
                setSaved('flex')
                setModalOpen(false)
            })
            .catch((error) => {
                // Zonder deze melding blijft de modal gewoon staan en denkt de
                // gebruiker dat het gelukt is.
                console.error('Opslaan van de vragenselectie is mislukt', error)
                setSaveError('Opslaan is niet gelukt. Probeer het opnieuw.')
            })
    }

    // Zoeken en filteren werken op het concept, zodat de tellingen meelopen.
    const term = search.trim().toLowerCase()
    const matches = (text) => (text || '').toLowerCase().includes(term)

    const visibleQuestion = (question, sectionTitle) => {
        if (term && !matches(question.Question) && !matches(sectionTitle)) return false
        const isActive = isActiveQuestion(question.ID)
        if (filter === 'on') return isActive
        if (filter === 'off') return !isActive
        return true
    }

    const renderQuestion = (question, sectionTitle) => {
        const isLocked = lockedIds.has(question.ID)
        const isQualifying = qualifyingIds.has(question.ID)
        const isActive = isActiveQuestion(question.ID)

        return (
            <div key={question.ID} className={`mmq-question ${isActive ? '' : 'mmq-question-off'}`}>
                <div className="mmq-question-row">
                    <label className="mmq-check">
                        <input
                            type="checkbox"
                            checked={isActive}
                            disabled={isLocked || isQualifying}
                            onChange={() => toggleQuestion(question.ID)}
                        />
                        <span>
                            {isLocked && question.EffectId?.[0]
                                ? <EffectMeta item={question.EffectId[0]} />
                                : (question.Question || '(zonder titel)')}
                        </span>
                    </label>
                    <div className="mmq-question-meta">
                        {isLocked && <span className="mmq-badge mmq-badge-standard">Meetstandaard</span>}
                        {isQualifying && <span className="mmq-badge mmq-badge-qualifying">Kwalificering</span>}
                        <QuestionTypeTag type={question.Type} />
                    </div>
                </div>
            </div>
        )
    }

    const renderSection = (group) => {
        const visible = group.questions.filter((question) => visibleQuestion(question, group.section.Question))
        if (visible.length === 0) return null

        const activeCount = group.questions.filter((question) => isActiveQuestion(question.ID)).length
        const allActive = activeCount === group.questions.length && group.questions.length > 0
        const switchable = group.questions.filter((question) => !isFixed(question.ID))

        return (
            <div
                key={group.section.ID}
                className="mmq-section"
                // Kleur komt uit Firestore, dus via een CSS-variabele — zelfde
                // aanpak als de vragenlijstbouwer (.qb-section --sec).
                style={{ '--mmq-sec': group.section.Color || '#47acc3' }}
            >
                <div className="mmq-section-head">
                    <label className="mmq-check">
                        <input
                            type="checkbox"
                            checked={allActive}
                            disabled={switchable.length === 0}
                            // Half aangevinkt kan alleen via de DOM — er is geen attribuut voor.
                            ref={(element) => {
                                if (element) element.indeterminate = activeCount > 0 && !allActive
                            }}
                            onChange={() => toggleSection(group.questions)}
                        />
                        <span>{group.section.Question || '(zonder titel)'}</span>
                    </label>
                    <span className="mmq-section-count">
                        {activeCount} van {group.questions.length} vragen aan
                    </span>
                </div>
                {visible.map((question) => renderQuestion(question, group.section.Question))}
            </div>
        )
    }

    const content = (
        <div className="mmq">
            {hasResponses && (
                <p className="mmq-notice">
                    Er is op dit meetmoment al ingevuld. Vragen uitzetten verwijdert bestaande
                    antwoorden niet, maar er komen geen nieuwe antwoorden meer bij.
                </p>
            )}

            {hasLocked && (
                <p className="mmq-question-note mmq-lock-note">
                    Meetstandaardvragen staan op elk meetmoment aan — het effect wordt afgemeten aan het
                    verschil tussen de voor- en de nameting. Alleen je eigen vragen zijn uit te zetten.
                </p>
            )}

            {qualifyingIds.size > 0 && (
                <p className="mmq-question-note mmq-lock-note">
                    Kwalificeringsvragen worden alleen op het eerste meetmoment gesteld. Aan en uit zet je bij het onderzoek, onder 'Wie doet mee'.
                </p>
            )}

            {summary.total === 0 ? (
                <p className="mmq-empty">Deze vragenlijst heeft nog geen vragen.</p>
            ) : nothingToChoose ? (
                <p className="mmq-empty">
                    {qualifyingIds.size > 0
                        ? 'Deze vragenlijst heeft geen vragen die je hier aan of uit kunt zetten.'
                        : 'Deze vragenlijst bestaat alleen uit meetstandaardvragen. Daar valt niets aan of uit te zetten.'}
                </p>
            ) : (
                <>
                    <div className="mmq-toolbar">
                        <div className="mmq-toolbar-row mmq-toolbar-copy-row">
                            <label className="mmq-copy" htmlFor="mmq-copy-select">Overnemen van…</label>
                            <select id="mmq-copy-select" value="" onChange={copyFromMoment}>
                                <option value="">-- kies meetmoment --</option>
                                {siblingMoments
                                    .filter((sibling) => sibling.ID !== moment?.ID)
                                    .map((sibling) => (
                                        <option key={sibling.ID} value={sibling.ID}>
                                            {sibling.Title || '(zonder titel)'}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="mmq-toolbar-row">
                        <div className="mmq-filters">
                            <button
                                type="button"
                                className={filter === 'all' ? 'mmq-filter mmq-filter-active' : 'mmq-filter'}
                                onClick={() => setFilter('all')}
                            >
                                Alle {summary.total}
                            </button>
                            <button
                                type="button"
                                className={filter === 'on' ? 'mmq-filter mmq-filter-active' : 'mmq-filter'}
                                onClick={() => setFilter('on')}
                            >
                                Aan {summary.active}
                            </button>
                            <button
                                type="button"
                                className={filter === 'off' ? 'mmq-filter mmq-filter-active' : 'mmq-filter'}
                                onClick={() => setFilter('off')}
                            >
                                Uit {summary.total - summary.active}
                            </button>
                        </div>
                        <button type="button" className="mmq-link-button" onClick={allOn}>Alles aan</button>
                        </div>

                        <input
                            type="text"
                            className="mmq-search"
                            placeholder="Zoek een vraag…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="mmq-list" role="group" aria-label="Vragen">
                        {groups.map((group) => (
                            group.kind === 'section'
                                ? renderSection(group)
                                : (visibleQuestion(group.question, '') ? renderQuestion(group.question, '') : null)
                        ))}
                    </div>

                </>
            )}
        </div>
    )

    // De stand hoort naast de knoppen: in de inhoud scrollt hij weg bij een
    // lange vragenlijst, en juist dán wil je zien wat je aan het doen bent.
    const actions = (
        <div className="mmq-actions">
            <div className="mmq-actions-status">
                {summary.total > 0 && (
                    <span className="mmq-footer-count">
                        {summary.active} van {summary.total} vragen aan
                    </span>
                )}
                {offEffectQuestions > 0 && (
                    <span className="mmq-footer-note">
                        {offEffectQuestions === 1
                            ? '1 uitgezette vraag is gekoppeld aan een effect. Dat effect is op dit meetmoment niet te vergelijken met andere meetmomenten.'
                            : `${offEffectQuestions} uitgezette vragen zijn gekoppeld aan een effect. Die effecten zijn op dit meetmoment niet te vergelijken met andere meetmomenten.`}
                    </span>
                )}
                {lostTriggers.length > 0 && (
                    <span className="mmq-footer-note">
                        {lostTriggers.length === 1
                            ? `'${lostTriggers[0].dependent.Question}' wordt normaal alleen getoond na een bepaald antwoord op '${lostTriggers[0].trigger.Question}'. Die vraag staat uit, dus '${lostTriggers[0].dependent.Question}' wordt op dit meetmoment aan iedereen gesteld.`
                            : `${lostTriggers.length} vragen worden normaal alleen na een bepaald antwoord getoond, maar de vraag waar ze van afhangen staat uit. Ze worden op dit meetmoment aan iedereen gesteld.`}
                    </span>
                )}
                {saveError && <span className="mmq-footer-warning">{saveError}</span>}
                {summary.total > 0 && summary.active === 0 && (
                    <span className="mmq-footer-warning">
                        Alle vragen staan uit — respondenten krijgen een lege vragenlijst.
                    </span>
                )}
            </div>
            <div className="mmq-actions-buttons">
                <button id="modal-cancel-button" onClick={() => setModalOpen(false)}>Annuleren</button>
                <button id="modal-save-button" onClick={save} disabled={!dirty}>Opslaan</button>
            </div>
        </div>
    )

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            actions={actions}
            pageHeader={`Vragen voor '${moment?.Title || 'meetmoment'}'`}
            description="Vink de vragen aan die respondenten op dit meetmoment krijgen. Uitgevinkte vragen worden overgeslagen. Andere meetmomenten veranderen niet."
            content={content}
        />
    )
}

export default MeasureMomentQuestionsModal
